import {
  ID,
  overdueSectionID,
  SortingMode,
  Task,
  TaskType,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  formatDateKey,
  getDueTaskPlanning,
  getSortedTasks,
  getTaskPlanning,
} from '@griply/utils';
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfYear,
  isAfter,
  isBefore,
  isSameMonth,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { useMemo } from 'react';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';

export type UpcomingDeadlineTreeOptions = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  dateFilterEndDate?: Timestamp | null;
  hideNewTaskRow?: boolean;
  shouldHaveRestOfMonth: boolean;
  restOfMonthStartDate: Timestamp;
};

export type UpcomingSection = {
  id: ID;
  startDate: Timestamp;
  endDate: Timestamp;
  isDay: boolean;
};

/**
 * Section ID prefixes for non-day sections.
 * Day sections use the standard formatDateKey format (YYYY-MM-DD).
 */
export const upcomingRestOfMonthPrefix = 'rom:';
export const upcomingMonthPrefix = 'month:';
export const upcomingYearPrefix = 'year:';

const buildSections = (
  today: Timestamp,
  shouldHaveRestOfMonth: boolean,
  restOfMonthStartDate: Timestamp,
  maxDate: Timestamp,
): UpcomingSection[] => {
  const sections: UpcomingSection[] = [];

  // Overdue section
  sections.push({
    id: overdueSectionID,
    startDate: new Date(0),
    endDate: endOfDay(addDays(today, -1)),
    isDay: true,
  });

  // 10 individual day sections (today + 9 days)
  sections.push(
    ...Array.from({ length: 10 }, (_, i) => {
      const day = addDays(today, i);
      return {
        id: formatDateKey(endOfDay(day)),
        startDate: startOfDay(day),
        endDate: endOfDay(day),
        isDay: true,
      };
    }),
  );

  // RestOfMonth (conditional)
  if (shouldHaveRestOfMonth) {
    const romStart = startOfDay(restOfMonthStartDate);
    const romEnd = endOfMonth(restOfMonthStartDate);
    sections.push({
      id: `${upcomingRestOfMonthPrefix}${formatDateKey(romStart)}`,
      startDate: romStart,
      endDate: romEnd,
      isDay: false,
    });
  }

  // Monthly sections
  const monthCounter =
    shouldHaveRestOfMonth && !isSameMonth(restOfMonthStartDate, today) ? 1 : 0;

  const monthSections = Array.from({ length: 10 }, (_, i) => {
    const monthDate = addMonths(today, monthCounter + i + 1);
    const mStart = startOfMonth(monthDate);
    const mEnd = endOfMonth(monthDate);
    return { mStart, mEnd };
  });

  monthSections.every(({ mStart, mEnd }) => {
    // Don't add months beyond maxDate
    if (isAfter(mStart, maxDate)) {
      return false;
    }

    sections.push({
      id: `${upcomingMonthPrefix}${formatDateKey(mStart)}`,
      startDate: mStart,
      endDate: mEnd,
      isDay: false,
    });

    return true;
  });

  // Year section
  const yearDate = addMonths(today, monthCounter + 11);
  const yearStart = startOfMonth(yearDate);
  const yearEnd = endOfYear(yearDate);

  if (!isAfter(yearStart, maxDate)) {
    sections.push({
      id: `${upcomingYearPrefix}${formatDateKey(yearStart)}`,
      startDate: yearStart,
      endDate: yearEnd,
      isDay: false,
    });
  }

  return sections;
};

const findSectionForDate = (
  date: Timestamp,
  sections: UpcomingSection[],
): UpcomingSection | undefined => {
  // Check overdue first
  if (isBefore(date, sections[1].startDate)) {
    return sections[0]; // overdue
  }

  // Check each section's date range
  return sections
    .slice(1)
    .find(
      (section) =>
        !isBefore(date, section.startDate) && !isAfter(date, section.endDate),
    );
};

export const useUpcomingDeadlineTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  dateFilterEndDate,
  hideNewTaskRow,
  shouldHaveRestOfMonth,
  restOfMonthStartDate,
}: UpcomingDeadlineTreeOptions): Record<ID, TreeItem> => {
  // Buckets: expensive per-task occurrence expansion (13-month window).
  // Deps exclude customOrder/sortingMode so reorders don't re-expand schedules.
  // See upcoming.invariants.md §5.
  const buckets = useMemo(() => {
    const maxDate = dateFilterEndDate ?? endOfYear(addMonths(today, 12));
    const sections = buildSections(
      today,
      shouldHaveRestOfMonth,
      restOfMonthStartDate,
      maxDate,
    );

    const overdueDeadline = endOfDay(addDays(today, -1));

    const sectionTaskIds: Record<ID, ID[]> = {};
    const sectionTasks: Record<ID, Task[]> = {};
    sections.forEach((section) => {
      sectionTaskIds[section.id] = [];
      sectionTasks[section.id] = [];
    });

    const usedInNonDaySection = new Set<ID>();

    tasks.forEach((task) => {
      const effectiveDeadline =
        !task.schedules?.length &&
        (task.endStrategy?.deadline || task.deadlineDeadline)
          ? new Date(
              Math.min(
                ...([
                  task.endStrategy?.deadline?.getTime(),
                  task.deadlineDeadline?.getTime(),
                ].filter(
                  (timestamp) => typeof timestamp === 'number',
                ) as number[]),
              ),
            )
          : null;

      const overduePlanning = getDueTaskPlanning(task, {
        date: overdueDeadline,
        today,
        weekStartsOn,
      });

      const duePlanning = overduePlanning.filter(
        (plannedOccurrence) =>
          !isAfter(plannedOccurrence.date, overdueDeadline),
      );

      const plannedOccurrence = duePlanning[duePlanning.length - 1];
      const isOverdue =
        !!plannedOccurrence ||
        (!!effectiveDeadline && !isAfter(effectiveDeadline, overdueDeadline));

      if (isOverdue) {
        sectionTaskIds[overdueSectionID].push(task.id);
        sectionTasks[overdueSectionID].push(task);
      }

      const taskDates = new Set<string>();

      if (effectiveDeadline && !isBefore(effectiveDeadline, today)) {
        const shouldSkip =
          dateFilterEndDate &&
          isAfter(effectiveDeadline, dateFilterEndDate) &&
          task.type !== TaskType.Task;

        if (!shouldSkip) {
          taskDates.add(effectiveDeadline.toISOString());
        }
      }

      if (!effectiveDeadline && task.schedules?.length) {
        // Day sections are always contiguous (today..today+9), so expand
        // them in a single merged call rather than one call per day — every
        // getTaskPlanning call re-parses the rrule and rebuilds timeframes,
        // so fewer calls = less fixed overhead. See upcoming.invariants.md §5.
        const daySectionsEndDate = endOfDay(addDays(today, 9));
        const dayOccurrences = getTaskPlanning(task, {
          startDate: startOfDay(today),
          endDate: daySectionsEndDate,
          today,
          weekStartsOn,
        });
        dayOccurrences.forEach(({ date }) => {
          if (dateFilterEndDate && isAfter(date, dateFilterEndDate)) {
            return;
          }
          taskDates.add(date.toISOString());
        });

        // Non-day sections: iterate in chronological order, narrow range
        // per section, break on first hit (dedup places the task in
        // exactly one non-day section).
        for (const section of sections) {
          if (section.isDay) {
            continue;
          }

          const sectionEndDate =
            dateFilterEndDate && isBefore(dateFilterEndDate, section.endDate)
              ? dateFilterEndDate
              : section.endDate;

          if (isBefore(sectionEndDate, section.startDate)) {
            break;
          }

          const sectionOccurrences = getTaskPlanning(task, {
            startDate: section.startDate,
            endDate: sectionEndDate,
            today,
            weekStartsOn,
          });

          if (sectionOccurrences.length) {
            taskDates.add(sectionOccurrences[0].date.toISOString());
            break;
          }
        }
      }

      taskDates.forEach((dateIso) => {
        const date = new Date(dateIso);
        const section = findSectionForDate(date, sections);

        if (!section) {
          return;
        }

        if (!section.isDay) {
          if (usedInNonDaySection.has(task.id)) {
            return;
          }
          usedInNonDaySection.add(task.id);
        }

        if (!sectionTaskIds[section.id].includes(task.id)) {
          sectionTaskIds[section.id].push(task.id);
          sectionTasks[section.id].push(task);
        }
      });
    });

    return { sections, sectionTasks };
  }, [
    tasks,
    today,
    weekStartsOn,
    dateFilterEndDate,
    shouldHaveRestOfMonth,
    restOfMonthStartDate,
  ]);

  return useMemo(() => {
    const { sections, sectionTasks } = buckets;

    const treeItems: Record<ID, TreeItem> = {};
    const sectionIds: ID[] = [];

    sections.forEach((section) => {
      const tasksInSection = sectionTasks[section.id];

      // Use the section's OWN custom order for sorting. A flattened order
      // across all sections would be wrong on Upcoming because habits appear
      // in multiple day sections — `order.indexOf(taskId)` returns the first
      // occurrence, so the earliest section's order would override every
      // later section's. See upcoming.invariants.md §5.
      const sectionCustomOrder =
        sortingMode === SortingMode.Custom
          ? customOrder?.[section.id] ?? []
          : [];

      const sortedTasks = getSortedTasks(tasksInSection, {
        today,
        weekStartsOn,
        order: sectionCustomOrder,
        mode:
          section.id === overdueSectionID
            ? [SortingMode.DueDate]
            : sortingMode
              ? [SortingMode.DueDate, sortingMode]
              : undefined,
        allDayFirst: true,
        invertedTimeslots: section.id === overdueSectionID,
      });

      treeItems[section.id] = {
        id: section.id,
        type: TreeItemTypes.Date,
        name: section.id,
        children: [],
        isFolder: true,
      };
      sectionIds.push(section.id);

      sortedTasks.forEach((task) => {
        treeItems[section.id].children!.push(task.id);
        treeItems[task.id] = {
          id: task.id,
          name: task.name,
          type: TreeItemTypes.Task,
        };
      });

      if (!hideNewTaskRow && section.isDay && section.id !== overdueSectionID) {
        const newTaskItemId = `${section.id}-new-task`;
        treeItems[section.id].children!.push(newTaskItemId);
        treeItems[newTaskItemId] = {
          id: newTaskItemId,
          name: 'New Task',
          type: TreeItemTypes.NewTask,
        };
      }
    });

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children: sectionIds,
      },
      ...treeItems,
    };
  }, [buckets, sortingMode, customOrder, hideNewTaskRow, today, weekStartsOn]);
};
